import { Component } from '@theme/component';
import { StandardEvents } from '@shopify/events';
import { DrawerOpenEvent } from '@theme/theme-drawer';

/**
 * A custom element that manages cart drawer behavior within a `<theme-drawer>`.
 *
 * Dialog lifecycle (open/close, squeeze, history, animations) is owned by `<theme-drawer>`.
 * The `cart:view` event is auto-dispatched by `CartItemsComponent` via the
 * `view-event-trigger="dialog"` attribute (see `snippets/cart-items-component.liquid`).
 * Cart count announcements are owned by `<header-actions>`.
 * This component handles the remaining cart-specific concerns: auto-open on add-to-cart,
 * sticky summary layout, and the installments CTA close-on-click.
 *
 * Brivanaa COD bridge:
 * - keeps normal Add to Cart behavior intact;
 * - hides Shopify's native/accelerated checkout actions inside the drawer only;
 * - lets Madgic/Orichi receive the shopper's ORIGINAL trusted click;
 * - releases Horizon's drawer focus/modal ownership immediately after that click,
 *   so the COD form can accept focus and keyboard input;
 * - never changes cart lines, variants, quantities, or purchase tracking.
 *
 * @extends {Component}
 */
class CartDrawerComponent extends Component {
  /** @type {number} */
  #summaryThreshold = 0.5;

  /** @type {AbortController} */
  #codBridgeAbortController = new AbortController();

  /** @type {import('@theme/theme-drawer').ThemeDrawer | null} */
  get #themeDrawer() {
    return /** @type {import('@theme/theme-drawer').ThemeDrawer | null} */ (this.closest('theme-drawer'));
  }

  /** @type {HTMLDialogElement | null} */
  get #dialog() {
    return this.closest('dialog');
  }

  connectedCallback() {
    super.connectedCallback();
    document.addEventListener(StandardEvents.cartLinesUpdate, this.#handleCartLinesUpdate);
    this.#themeDrawer?.addEventListener(DrawerOpenEvent.eventName, this.#handleDrawerOpen);

    // IMPORTANT: Never cancel or replay the COD click. Third-party COD apps can
    // rely on the original trusted pointer/click event to initialise their form.
    // We only observe the click, allow it to reach Madgic normally, then release
    // the Shopify drawer/focus trap on the next task.
    this.#dialog?.addEventListener('click', this.#handleCodTriggerClick, {
      capture: true,
      signal: this.#codBridgeAbortController.signal,
    });

    this.#prepareCodDrawer();

    // The restore path sets [open] before this module loads, so the
    // theme-drawer:open event will have already fired. Use the attribute
    // check so this works even before <theme-drawer> upgrades.
    if (this.#themeDrawer?.hasAttribute('open')) {
      this.#handleDrawerOpen();
    }
  }

  disconnectedCallback() {
    super.disconnectedCallback();
    document.removeEventListener(StandardEvents.cartLinesUpdate, this.#handleCartLinesUpdate);
    this.#themeDrawer?.removeEventListener(DrawerOpenEvent.eventName, this.#handleDrawerOpen);
    this.#codBridgeAbortController.abort();
  }

  /**
   * Hide only Shopify's native checkout actions inside this cart drawer.
   * Madgic's COD CTA is intentionally left untouched.
   */
  #prepareCodDrawer = () => {
    const nativeCheckout = this.querySelector('#checkout[name="checkout"], .cart__checkout-button[name="checkout"]');
    if (nativeCheckout instanceof HTMLElement) {
      nativeCheckout.hidden = true;
      nativeCheckout.setAttribute('aria-hidden', 'true');
      nativeCheckout.setAttribute('tabindex', '-1');
    }

    for (const element of this.querySelectorAll(
      '.additional-checkout-buttons, .cart__additional-checkout-buttons, shopify-accelerated-checkout-cart'
    )) {
      if (element instanceof HTMLElement) {
        element.hidden = true;
        element.setAttribute('aria-hidden', 'true');
      }
    }
  };

  /**
   * Detect the existing Madgic/Orichi COD action without hard-coding a single
   * app-generated DOM selector. The app can change its internal markup while
   * keeping its app name/COD button label.
   *
   * @param {Element} element
   */
  #isCodTrigger(element) {
    if (!element.matches('button, a, [role="button"], input[type="button"], input[type="submit"]')) return false;
    if (element.matches('#checkout, [name="checkout"], .cart__checkout-button')) return false;

    const htmlElement = /** @type {HTMLElement} */ (element);
    const label = [
      htmlElement.id,
      htmlElement.className,
      htmlElement.getAttribute('aria-label'),
      htmlElement.getAttribute('title'),
      htmlElement.getAttribute('data-testid'),
      htmlElement.getAttribute('data-action'),
      htmlElement.textContent,
      element instanceof HTMLInputElement ? element.value : '',
    ]
      .filter(Boolean)
      .join(' ')
      .toLowerCase();

    return /madgic|orichi|cash\s*on\s*delivery|\bcod\b|الدفع\s*عند\s*الاستلام|اطلب\s*الآن/.test(label);
  }

  /**
   * Preserve Madgic's original trusted click. Once the current click dispatch
   * has finished, close the Horizon cart drawer. On desktop this immediately
   * removes Horizon's JS focus trap; on mobile it releases the modal/top-layer
   * owner through the normal drawer close lifecycle.
   *
   * @param {MouseEvent} event
   */
  #handleCodTriggerClick = (event) => {
    const path = event.composedPath();
    const trigger = path.find((node) => node instanceof Element && this.#isCodTrigger(node));
    if (!(trigger instanceof HTMLElement)) return;

    // DO NOT preventDefault(), stopPropagation(), stopImmediatePropagation(),
    // or call trigger.click(). The real shopper click must reach Madgic.
    setTimeout(() => {
      void this.#themeDrawer?.close();
    }, 0);
  };

  /**
   * Handles the theme-drawer opening — updates sticky state and wires up the installments CTA.
   */
  #handleDrawerOpen = () => {
    this.#prepareCodDrawer();
    this.#updateStickyState();

    // Close cart drawer when installments CTA is clicked to avoid overlapping dialogs.
    // Re-queried on every open so it survives cart content re-renders that
    // replace the shopify-payment-terms shadow root.
    customElements.whenDefined('shopify-payment-terms').then(() => {
      const cta = this.querySelector('shopify-payment-terms')?.shadowRoot?.querySelector('#shopify-installments-cta');
      cta?.addEventListener('click', () => this.#themeDrawer?.close(), { once: true });
    });
  };

  /**
   * @param {import('@shopify/events').CartLinesUpdateEvent} event
   */
  #handleCartLinesUpdate = (event) => {
    const shouldAutoOpen = this.hasAttribute('auto-open') && event.action === 'add' && !this.#themeDrawer?.isOpen;

    // When the event originates inside an open MODAL <dialog> (e.g. quick-add),
    // defer the auto-open until that dialog's native `close` fires so its focus
    // restoration runs first — otherwise we'd capture the wrong
    // `#previouslyFocused`. Non-modal dialogs (e.g. the hotspot preview) don't
    // close on add and don't move focus, so `:modal` excludes them.
    const sourceModal = /** @type {HTMLDialogElement | null} */ (
      event.target instanceof Element ? event.target.closest('dialog:modal') : null
    );

    if (shouldAutoOpen && !sourceModal && !this.#isCartEmpty()) {
      this.#themeDrawer?.open();
    }

    event.promise
      ?.then(({ detail }) => {
        const settle = () =>
          requestAnimationFrame(() => {
            this.#prepareCodDrawer();
            this.#updateStickyState();
          });

        if (!shouldAutoOpen || detail?.didError) {
          settle();
          return;
        }

        const openAndSettle = () => {
          if (!this.#themeDrawer?.isOpen) this.#themeDrawer?.open();
          settle();
        };

        if (sourceModal?.open) {
          sourceModal.addEventListener('close', openAndSettle, { once: true });
        } else {
          openAndSettle();
        }
      })
      .catch((error) => {
        if (error?.name !== 'AbortError') console.warn('[cart-drawer] Event promise rejected:', error);
      });
  };

  #isCartEmpty() {
    return Boolean(this.querySelector('.cart-drawer--empty'));
  }

  #updateStickyState() {
    const dialog = this.#dialog;
    if (!dialog) return;

    // Refs do not cross nested `*-component` boundaries (e.g., `cart-items-component`), so we query within the dialog.
    const content = dialog.querySelector('.cart-drawer__content');
    const summary = dialog.querySelector('.cart-drawer__summary');

    if (!content || !summary) {
      // Ensure the dialog doesn't get stuck in "unsticky" mode when summary disappears (e.g., empty cart).
      dialog.setAttribute('cart-summary-sticky', 'false');
      return;
    }

    const drawerHeight = dialog.getBoundingClientRect().height;
    const summaryHeight = summary.getBoundingClientRect().height;
    const ratio = summaryHeight / drawerHeight;
    dialog.setAttribute('cart-summary-sticky', ratio > this.#summaryThreshold ? 'false' : 'true');
  }
}

if (!customElements.get('cart-drawer-component')) {
  customElements.define('cart-drawer-component', CartDrawerComponent);
}
